import { computed, ref, shallowRef } from 'vue'
import { defineStore } from 'pinia'

import type { VendorEvent } from '@/utils/vendors/types'
import { getTeslaEventInfo } from '@/utils/vendors/tesla'
import { parseTeslaSelection, type PickNote } from '@/utils/vendors/tesla-flat'
import {
  detectDriveProblem,
  groupEventsByDay,
  toRows,
  type DriveProblem
} from '@/utils/vendors/tesla-navigator'

export type PickMode = 'folder' | 'files'

export const useTeslaCamStore = defineStore('teslacam', () => {
  const folderName = ref<string | null>(null)
  const isParsing = ref(false)
  const problem = ref<DriveProblem | null>(null)
  // 'files' when the user picked an event's files rather than a folder.
  const pickMode = ref<PickMode>('folder')
  const pickNotes = ref<PickNote[]>([])
  // shallowRef: events hold File objects, which must not be wrapped in reactive proxies.
  const events = shallowRef<VendorEvent[]>([])

  const dayGroups = computed(() => groupEventsByDay(events.value))
  const rows = computed(() => toRows(dayGroups.value))
  const countsByCategory = computed(() => {
    const counts: Record<string, number> = {}
    for (const event of events.value) {
      const { category } = getTeslaEventInfo(event)
      if (category) counts[category] = (counts[category] ?? 0) + 1
    }
    return counts
  })

  function clear() {
    folderName.value = null
    problem.value = null
    pickMode.value = 'folder'
    pickNotes.value = []
    events.value = []
  }

  /** Reads a picked folder. Browse only: nothing is uploaded or written to the drive. */
  async function loadFiles(files: File[], name: string, mode: PickMode = 'folder') {
    clear()
    pickMode.value = mode
    folderName.value = name
    isParsing.value = true
    try {
      problem.value = await detectDriveProblem(files)
      if (problem.value) return
      const selection = await parseTeslaSelection(files)
      events.value = selection.events
      pickNotes.value = selection.notes
      if (events.value.length === 0) problem.value = 'empty'
    } catch (err) {
      console.error('TeslaCam parse error', err)
      problem.value = 'unreadable'
    } finally {
      isParsing.value = false
    }
  }

  return {
    folderName,
    isParsing,
    problem,
    pickMode,
    pickNotes,
    events,
    dayGroups,
    rows,
    countsByCategory,
    loadFiles,
    clear
  }
})
